import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { cn } from '@/app/lib/utils';
import Icon from './Icon';
import { FocusScope } from '@radix-ui/react-focus-scope';
// import Icon from '../Icon';

const Dialog = DialogPrimitive.Root;

const DialogPortal = DialogPrimitive.Portal;

export type ModalProps = React.PropsWithChildren<{
  isOpen?: boolean;
  bodyClassName?: string;
  wrapperClassName?: string;
  showClose?: boolean;
  closeButtonClassName?: string;
  closeOnClickOutside?: boolean;
  header?: React.ReactNode;
  title?: React.ReactNode;
  titleIcon?: React.ReactNode;
  subtitle?: React.ReactNode;
  footer?: React.ReactNode;
  footerClassName?: string;
  hasScroll?: boolean;
  viewportClassName?: string;
  rootClassName?: string;
  scrollClassName?: string;
  headerClassName?: string;
  animation?: string[];
  modal?: boolean;
  zIndex?: number;
  onBeforeClose?: () => boolean | Promise<boolean>;
  onOpenChange?: (open: boolean) => void;
}>;

export type ModalRef = {
  open: () => void;
  close: () => void;
};

/** Generic function that prevents any default event behavior. */
const avoidDefaultDomBehavior = (e: Event) => {
  e.preventDefault();
};

const Modal = React.forwardRef<ModalRef, ModalProps>(
  (
    {
      isOpen,
      showClose,
      closeButtonClassName,
      bodyClassName,
      wrapperClassName,
      scrollClassName,
      closeOnClickOutside,
      onOpenChange,
      children,
      title,
      header,
      titleIcon,
      subtitle,
      footer,
      hasScroll = false,
      viewportClassName,
      rootClassName,
      headerClassName,
      animation = ['in', 'out'],
      footerClassName,
      modal = false,
      zIndex,
      onBeforeClose,
    },
    ref
  ) => {
    const [isModelOpen, setOpen] = React.useState(false);

    React.useImperativeHandle(ref, () => {
      return {
        open: () => {
          setOpen(true);
        },
        close: () => {
          setOpen(false);
        },
      };
    });

    React.useEffect(() => {
      if (isOpen !== undefined) setOpen(isOpen);
    }, [isOpen]);

    const handleOpenChange = React.useCallback(
      (open: boolean) => {
        if (open) {
          setOpen(true);
          onOpenChange?.(true);
          return;
        }

        const close = () => {
          setOpen(false);
          onOpenChange?.(false);
        };

        const canClose = onBeforeClose?.();
        if (canClose instanceof Promise) {
          void canClose
            .then(allowed => {
              if (allowed !== false) {
                close();
              }
            })
            .catch(console.error);
          return;
        }

        if (canClose === false) {
          return;
        }

        close();
      },
      [onBeforeClose, onOpenChange]
    );

    return (
      <Dialog open={isModelOpen} modal={modal} onOpenChange={handleOpenChange} aria-hidden={false}>
        <DialogPortal>
          <div
            aria-hidden="true"
            data-state={isModelOpen ? 'open' : 'closed'}
            className="fixed pointer-events-auto inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:duration-300 data-[state=open]:duration-300"
            style={zIndex !== undefined ? { zIndex } : undefined}
            onClick={() => {
              if (closeOnClickOutside) {
                handleOpenChange(false);
              }
            }}
          />
          <FocusScope trapped={false}>
            <DialogPrimitive.Content
              onPointerDownOutside={avoidDefaultDomBehavior}
              onInteractOutside={avoidDefaultDomBehavior}
              onOpenAutoFocus={e => e.preventDefault()}
              onEscapeKeyDown={e => e.preventDefault()}
              className={cn(
                'fixed !flex flex-col left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] bg-popover p-6 shadow-lg rounded sm:max-w-[425px] min-h-300px !overflow-hidden focus:outline-none',
                animation.includes('in') &&
                  'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
                animation.includes('out') &&
                  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
                hasScroll && 'max-h-[calc(100vh-40px)]',
                title ? 'px-0' : '',
                bodyClassName
              )}
              style={zIndex !== undefined ? { zIndex: zIndex + 1 } : undefined}
            >
              {header ? (
                <div className="border-b border-border">{header}</div>
              ) : title ? (
                <div className="border-b border-border pb-5">
                  <div className="flex gap-1 items-center">
                    {titleIcon && <div>{titleIcon}</div>}
                    <div>
                      <DialogPrimitive.Title className={cn('font-semibold px-4', headerClassName)}>
                        {title}
                      </DialogPrimitive.Title>
                      {subtitle && <p className="text-sm text-muted-foreground px-4">{subtitle}</p>}
                      <DialogPrimitive.Description />
                    </div>
                  </div>
                </div>
              ) : (
                <VisuallyHidden.Root>
                  <DialogPrimitive.Title />
                  <DialogPrimitive.Description />
                </VisuallyHidden.Root>
              )}
              {hasScroll && (
                <div
                  className={cn(
                    'max-h-[calc(100vh-180px)] min-h-0 touch-pan-y overflow-y-auto overscroll-contain',
                    scrollClassName,
                    rootClassName
                  )}
                >
                  <div className={cn('p-4', viewportClassName)}>{children}</div>
                </div>
              )}
              {!hasScroll && (
                <div className={cn(title ? 'flex-1' : '', wrapperClassName)}>{children}</div>
              )}
              {showClose && (
                <DialogPrimitive.Close
                  className={cn(
                    'absolute right-4 w-8 h-8 top-4 rounded-sm text-foreground opacity-70 transition-opacity hover:opacity-100 hover:bg-secondary focus:outline-none disabled:pointer-events-none cursor-pointer flex items-center justify-center',
                    closeButtonClassName
                  )}
                >
                  <Icon name="cross-fill" className="h-5 w-5" />
                </DialogPrimitive.Close>
              )}
              {footer && (
                <div className={cn('border-t border-border pt-4 px-5', footerClassName)}>
                  {footer}
                </div>
              )}
            </DialogPrimitive.Content>
          </FocusScope>
        </DialogPortal>
      </Dialog>
    );
  }
);

Modal.displayName = 'Modal';

export default Modal;
